import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/common/Logo";
import { Icon } from "@/components/common/Icon";
import { useAuth } from "@/context/AuthContext";

const NavList = ({ items, onNavigate }) => (
  <nav className="flex flex-col gap-1">
    {items.map((it) => (
      <NavLink
        key={it.to}
        to={it.to}
        end={it.end}
        onClick={onNavigate}
        data-testid={`shell-nav-${it.key}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`
        }
      >
        <Icon name={it.icon} className="h-4 w-4" /> {it.label}
      </NavLink>
    ))}
  </nav>
);

const SideContent = ({ items, badge, onNavigate }) => (
  <div className="flex h-full flex-col bg-[#123634] p-5">
    <Logo dark testid="shell-logo-link" />
    <span className="mono mt-3 w-fit rounded border border-white/15 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-[#E8927F]">{badge}</span>
    <div className="mt-8 flex-1"><NavList items={items} onNavigate={onNavigate} /></div>
    <NavLink to="/" className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:text-white" data-testid="shell-website-link"><ExternalLink className="h-3.5 w-3.5" /> Public website</NavLink>
  </div>
);

export const DashboardShell = ({ items, badge }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => {
    await logout();
    navigate("/login");
  };
  return (
    <div className="flex min-h-screen bg-[#F4F6F2]">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 lg:block"><SideContent items={items} badge={badge} /></aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white/85 px-4 backdrop-blur-xl sm:px-8">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="rounded-md p-2 lg:hidden" aria-label="Open navigation" data-testid="shell-mobile-menu-button"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 border-0 p-0">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SideContent items={items} badge={badge} onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900" data-testid="shell-company-name">{user?.company_name}</p>
              <p className="truncate text-xs text-slate-500">{user?.contact_name} · {user?.email}</p>
            </div>
          </div>
          <button onClick={doLogout} className="btn-outline py-2" data-testid="shell-logout-button"><LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span></button>
        </header>
        <main className="flex-1 px-4 py-8 sm:px-8"><Outlet /></main>
      </div>
    </div>
  );
};

export const PageTitle = ({ eyebrow, title, text, action }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">{title}</h1>
      {text && <p className="mt-1 text-sm text-slate-600">{text}</p>}
    </div>
    {action}
  </div>
);

export const StatCard = ({ label, value, icon, tone = "slate", testid }) => (
  <div className="card-base p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="mono text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <Icon name={icon} className={`h-4 w-4 ${tone === "red" ? "text-[#C20500]" : tone === "amber" ? "text-amber-600" : "text-slate-400"}`} />
    </div>
    <p className="mt-3 text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Fraunces" }}>{value ?? "—"}</p>
  </div>
);

export const EmptyState = ({ title, text, action, testid = "empty-state" }) => (
  <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid={testid}>
    <p className="font-semibold text-slate-900">{title}</p>
    {text && <p className="mt-1 text-sm text-slate-500">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);
